import { useState, useEffect } from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  ShieldCheck, CheckCircle, ChevronDown, ChevronUp,
  Lock, Users, Building, Landmark, ArrowRight, AlertTriangle,
  FileText, ClipboardCheck, MailOpen, Mail, Home, Briefcase,
  Scale, Shield, Clock, DollarSign, Search, Ban,
  TrendingDown, Award, MapPin, Quote, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useJsonLd } from '@/hooks/useJsonLd';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const stagger = {
  show: { transition: { staggerChildren: 0.1 } }
};

const CTA_URL = "https://app.kgob.com/irs-transcript-report";

// Build a CTA URL with a `source` query param for analytics attribution.
// Each placement gets its own value so GA4/GTM can attribute conversions later.
function ctaUrl(source) {
  return `${CTA_URL}?source=${encodeURIComponent(source)}`;
}

// ---- Inline CTA — restrained, editorial. Placed at intent-peak moments. ----
const IRS_REVIEW_SCHEMA = {
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "name": "KGOB IRS Account Review",
          "description": "Free IRS account review and tax resolution service. Get your IRS transcripts analyzed by CPAs at no cost and receive expert guidance on resolving tax notices, tax debt, and IRS compliance issues.",
          "url": "https://kgob.com/irs-account-review",
          "provider": {
            "@type": "AccountingService",
            "name": "KGOB CPAs & Advisors",
            "url": "https://kgob.com",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "2740 East WT Harris Blvd, Suite 200",
              "addressLocality": "Charlotte",
              "addressRegion": "NC",
              "postalCode": "28213",
              "addressCountry": "US"
            },
            "sameAs": [
              "https://www.linkedin.com/company/kgobcpa/",
              "https://www.instagram.com/kgobcpa",
              "https://x.com/kgobcpa"
            ]
          },
          "serviceType": [
            "IRS Account Review",
            "IRS Transcript Analysis",
            "Tax Notice Resolution",
            "Tax Debt Relief",
            "Offer in Compromise",
            "IRS Payment Plan",
            "IRS Representation",
            "Tax Resolution Services"
          ],
          "areaServed": {
            "@type": "Country",
            "name": "United States"
          },
          "potentialAction": {
            "@type": "Action",
            "target": "https://kgob.com/irs-account-review",
            "name": "Request Free IRS Account Review"
          }
        };

// ---- Inline CTA — restrained, editorial. Placed at intent-peak moments. ----
function InlineCTA({ source, eyebrow, title, helper, variant = 'light' }) {
  const isDark = variant === 'dark';

  return (
    <section
      className={`py-12 sm:py-14 ${isDark ? 'bg-[#0B2240]' : 'bg-white border-y border-[#E1DFD7]/60'}`}
      data-testid={`inline-cta-${source}`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl text-center">
        {eyebrow && (
          <div className="flex items-center justify-center gap-3 mb-3">
            <span className="h-px w-7 bg-[#D4AF37]" />
            <span className="font-['Inter'] text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] font-semibold">{eyebrow}</span>
            <span className="h-px w-7 bg-[#D4AF37]" />
          </div>
        )}
        <h3 className={`font-['Playfair_Display'] text-xl sm:text-2xl mb-6 ${isDark ? 'text-[#F7F6F2]' : 'text-[#0B2240]'}`}>
          {title}
        </h3>
        <Button data-testid="i-r-s-transcript-report-button-2"
          asChild
          className="h-12 px-7 rounded-xl bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] hover:brightness-110"
        >
          <a href={ctaUrl(source)} target="_blank" rel="noopener noreferrer" data-testid={`inline-cta-link-${source}`}>
            Get My Free Account Review
            <ArrowRight className="h-4 w-4 ml-2" />
          </a>
        </Button>
        {helper && (
          <p className={`mt-4 text-xs ${isDark ? 'text-[#F7F6F2]/55' : 'text-[#4C5A70]/70'}`}>
            {helper}
          </p>
        )}
      </div>
    </section>
  );
}

// ---- Sticky CTA — appears after scroll past 600px, hides when footer-CTA enters viewport ----
function StickyCTA() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem('kgob_irs_sticky_dismissed') === '1';
    } catch (e) {
      return false;
    }
  });

  useEffect(() => {
    if (dismissed) return undefined;
    const onScroll = () => {
      const scrolled = window.scrollY || document.documentElement.scrollTop;
      const footerCTA = document.querySelector('[data-testid="footer-cta"]');
      let footerInView = false;
      if (footerCTA) {
        const rect = footerCTA.getBoundingClientRect();
        // Hide sticky when the final CTA section is within 200px of being visible
        footerInView = rect.top < (window.innerHeight + 200);
      }
      setVisible(scrolled > 600 && !footerInView);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [dismissed]);

  if (dismissed || !visible) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem('kgob_irs_sticky_dismissed', '1');
    } catch (e) {
      // sessionStorage blocked — accept the hide for this view only
    }
  };

  return (
    <div
      className="fixed z-40 left-3 right-3 bottom-3 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm"
      data-testid="sticky-cta"
    >
      <div className="bg-[#0B2240] rounded-xl shadow-2xl border border-[#D4AF37]/30 overflow-hidden">
        <div className="px-4 sm:px-5 py-3 sm:py-4 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Lock className="w-3 h-3 text-[#D4AF37] flex-shrink-0" />
              <span className="text-[10px] tracking-[0.18em] uppercase text-[#D4AF37] font-semibold truncate">
                Free &middot; 38 yrs &middot; No obligation
              </span>
            </div>
            <Button data-testid="i-r-s-transcript-report-button-1"
              asChild
              className="w-full h-10 rounded-md bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] font-semibold text-sm hover:brightness-110"
            >
              <a
                href={ctaUrl('irs-review-sticky')}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sticky-cta-link"
              >
                Get My Free Account Review
                <ArrowRight className="h-3 w-3 ml-1" />
              </a>
            </Button>
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="text-white/40 hover:text-white/80 transition-colors p-1 -mr-1 flex-shrink-0"
            aria-label="Dismiss"
            data-testid="sticky-cta-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

const faqs = [
  {
    q: "How long does the IRS Account Review take?",
    a: "After you sign the authorization, the IRS typically processes it within a few business days. Once we have your records, our CPA team completes and delivers your written report promptly. We keep you updated along the way so you always know where things stand."
  },
  {
    q: "Is my information secure?",
    a: "Yes. All data is transmitted using 256-bit encryption and handled under strict professional confidentiality requirements. Your SSN/EIN is never stored in plain text. All communication happens through your secure portal."
  },
  {
    q: "Can you help me resolve what the review finds?",
    a: "Absolutely. The review itself is free. If it turns up issues, our CPA team can step in and work with the IRS on your behalf — whether that's reducing penalties, setting up a payment plan, or negotiating a settlement. Resolution work is a separate engagement, and what that costs depends on your specific situation. Your CPA will explain everything clearly before any work begins."
  },
  {
    q: "How do you access my IRS records?",
    a: "We retrieve your IRS transcripts using IRS Form 2848 (Power of Attorney and Declaration of Representative), the standard IRS-approved authorization that allows licensed tax professionals to access your account records on your behalf."
  },
  {
    q: "Does this cover businesses and trusts — not just individuals?",
    a: "Yes. We review IRS accounts for individuals, businesses of all types (LLC, S-Corp, C-Corp, partnerships), and trusts and estates. The IRS keeps different records for each — and our CPAs know exactly how to read them."
  },
  {
    q: "What if I think my taxes are fully in order?",
    a: "Many clients expect a clean account and get one — confirming their compliance with certainty. Others were confident everything was fine and discovered old penalties, lien filings, or IRS-prepared returns they didn't know about. Either way, you get certainty instead of assumption."
  },
  {
    q: "Is the IRS Account Review really free?",
    a: "Yes — completely free, no strings attached. A licensed CPA pulls your IRS records, analyzes them, and delivers a plain-English report at no cost to you. If the review uncovers issues you'd like help resolving (penalties, payment plans, settlements), that's a separate engagement with its own pricing. But the review itself? Always free."
  },
  {
    q: "Do you guarantee a specific outcome on my IRS case?",
    a: "No — and any firm that promises a specific result before reviewing your account is one to walk away from. What we guarantee is the Account Review itself: a complete, accurate, plain-English read of your IRS file, delivered by a licensed CPA. After 38 years in practice and over 2,000 IRS-resolution engagements, we know what's possible — but every case turns on facts only your transcript reveals. We do honest work, set realistic expectations, and pursue the strongest available path for your specific situation."
  }
];

const FAQItem = ({ faq, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <button
      onClick={() => setOpen(!open)}
      className={`w-full text-left p-5 rounded-xl border transition-all duration-200 ${
        open ? 'bg-white border-[#D4AF37]/30 shadow-sm' : 'bg-white/50 border-[#E1DFD7] hover:border-[#D4AF37]/20'
      }`}
      data-testid={`faq-item-${index}`}
      aria-expanded={open}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-semibold text-[#0B2240] text-sm sm:text-base">{faq.q}</h3>
        {open ? <ChevronUp className="h-5 w-5 text-[#D4AF37] flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-[#4C5A70] flex-shrink-0" />}
      </div>
      {open && <p className="mt-3 text-sm text-[#4C5A70] leading-relaxed">{faq.a}</p>}
    </button>
  );
};

const IRSTranscriptReport = () => {
  useJsonLd('irs-account-review', IRS_REVIEW_SCHEMA);
  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Helmet>
        <title>Free IRS Account Review | Tax Notice Help | KGOB CPAs & Advisors</title>
        <meta name="description" content="Get a free IRS account review by licensed CPAs. We analyze your IRS transcripts at no cost and provide resolution options for tax notices, tax debt, payment plans, and IRS relief." />
        <link rel="canonical" href="https://kgob.com/irs-account-review" />
        <meta property="og:title" content="Free IRS Account Review — KGOB CPAs & Advisors" />
        <meta property="og:description" content="Received an IRS notice? Get your account reviewed by professional CPAs — completely free. We analyze your IRS records and provide resolution options." />
        <meta property="og:url" content="https://kgob.com/irs-account-review" />
        <meta property="og:type" content="website" />
      </Helmet>

      <Header />

      <main>
        {/* ── HERO ── */}
        <section className="relative bg-gradient-to-b from-[#0B2240] via-[#0f2d4f] to-[#0B2240] py-20 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/15 flex items-center justify-center">
                  <ClipboardCheck className="w-8 h-8 text-[#D4AF37]" />
                </div>
              </div>

              <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F7F6F2] mb-6" data-testid="hero-headline">
                Your Free IRS Account Review Starts Here
              </h1>

              <p className="text-lg sm:text-xl text-[#F7F6F2]/80 mb-4 max-w-2xl mx-auto leading-relaxed">
                Our licensed CPAs pull everything the IRS has on your account and hand you a clear, plain-English report — at no cost to you. No tax code, no guessing. You&rsquo;ll know exactly where you stand. If anything needs fixing, our CPA team is right there to help.
              </p>

              <p className="text-sm sm:text-base text-[#D4AF37]/90 mb-8 max-w-2xl mx-auto leading-relaxed italic">
                Most firms charge $300&ndash;$900 for an IRS Account Review. After 38 years of doing this work, we&rsquo;d rather start the conversation.
              </p>

              <Button asChild className="h-14 px-8 rounded-xl bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] font-semibold text-base shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] hover:brightness-110" data-testid="hero-cta">
                <a data-testid="i-r-s-transcript-report-a-2" href={ctaUrl('irs-review-hero')} target="_blank" rel="noopener noreferrer">
                  Get Your Free IRS Account Review
                  <ArrowRight className="h-5 w-5 ml-2" />
                </a>
              </Button>

              <p className="mt-6 text-sm text-[#F7F6F2]/50 flex items-center justify-center gap-2">
                <Lock className="h-4 w-4" />
                100% Free. Prepared by licensed CPAs. Secure. Confidential.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ── VISUAL DEMO CARD ── */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="relative">
              <div className="rounded-2xl border border-[#0B2240]/10 bg-gradient-to-br from-[#0B2240] to-[#0f2d4f] p-6 sm:p-8 shadow-xl overflow-hidden">
                {/* Sample badge */}
                <div className="absolute top-4 right-4 bg-[#D4AF37]/20 text-[#D4AF37] text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                  Sample Report
                </div>

                <div className="flex items-center gap-2 mb-1">
                  <p className="text-white/40 text-xs">KGOB CPAs & Advisors</p>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-white mb-1">IRS Account Summary</h3>
                <p className="text-white/40 text-xs mb-6">CPA Reviewed · Confidential</p>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  {[
                    { label: "Account Status", value: "Action Required", color: "text-red-400" },
                    { label: "Balance Due", value: "$18,440", color: "text-white" },
                    { label: "Penalties on File", value: "$3,120", color: "text-white" },
                    { label: "Issues Flagged", value: "4 items", color: "text-[#D4AF37]" }
                  ].map((item, i) => (
                    <div key={i} className="bg-white/5 rounded-lg p-3">
                      <p className="text-white/40 text-[10px] uppercase tracking-wide mb-1">{item.label}</p>
                      <p className={`text-sm font-semibold ${item.color}`}>{item.value}</p>
                    </div>
                  ))}
                </div>

                <div className="bg-[#D4AF37]/10 rounded-lg p-4 border border-[#D4AF37]/20">
                  <div className="flex items-start gap-3">
                    <ShieldCheck className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-white text-sm font-medium">CPA Recommendation Included</p>
                      <p className="text-white/60 text-xs mt-1">Relief pathways identified — penalty abatement eligible, installment plan recommended.</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── TRUST BADGES ── */}
        <section className="py-8 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { icon: ShieldCheck, label: "Licensed CPA Reviews Every Report" },
                { icon: Users, label: "Individuals · Business · Trusts & Estates" },
                { icon: FileText, label: "Plain-English Written Report" },
                { icon: Lock, label: "Secure & Encrypted Portal" }
              ].map((badge, i) => {
                const Icon = badge.icon;
                return (
                  <div key={i} className="flex flex-col items-center gap-2">
                    <Icon className="h-5 w-5 text-[#D4AF37]" />
                    <p className="text-xs text-[#4C5A70] font-medium">{badge.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── SECTION A: THE NUMBERS (track record) ── */}
        <section className="py-20 bg-gradient-to-b from-[#0B2240] via-[#0f2d4f] to-[#0B2240]" data-testid="track-record-section">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.55 }} className="text-center mb-12">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="h-px w-8 bg-[#D4AF37]" />
                <span className="font-['Inter'] text-[11px] font-semibold tracking-[0.22em] uppercase text-[#D4AF37]">
                  Practicing since 1988 &middot; all 50 states
                </span>
                <span className="h-px w-8 bg-[#D4AF37]" />
              </div>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#F7F6F2] leading-tight">
                The reason we can give this report away.
              </h2>
            </motion.div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-10" data-testid="track-record-stats">
              {[
                { icon: Clock, value: "38", suffix: "years", label: "of firm practice since 1988" },
                { icon: Users, value: "2,000+", suffix: "", label: "IRS-resolution engagements" },
                { icon: TrendingDown, value: "$800M+", suffix: "", label: "in tax debt resolved for clients" },
                { icon: Search, value: "93%", suffix: "", label: "of reviews surface a material issue" },
              ].map((stat, i) => {
                const Icon = stat.icon;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.5, delay: 0.08 * i }}
                    className="bg-white/[0.04] border border-[#D4AF37]/20 rounded-lg p-6 text-center"
                    data-testid={`track-record-stat-${i}`}
                  >
                    <Icon className="w-6 h-6 text-[#D4AF37] mx-auto mb-3" />
                    <div className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#F7F6F2] leading-none mb-1">
                      {stat.value}
                    </div>
                    {stat.suffix && (
                      <div className="text-[11px] tracking-[0.16em] uppercase text-[#D4AF37] mb-2">{stat.suffix}</div>
                    )}
                    <p className="text-xs sm:text-sm text-[#F7F6F2]/65 leading-relaxed mt-2">{stat.label}</p>
                  </motion.div>
                );
              })}
            </div>

            <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.5, delay: 0.4 }} className="max-w-3xl mx-auto text-center">
              <p className="text-[#F7F6F2]/80 leading-relaxed text-sm sm:text-base">
                Most CPA firms charge <strong className="text-[#D4AF37]">$300&ndash;$900</strong> for an IRS Account Review. We don&rsquo;t. After 38 years of doing this work, we know exactly what these transcripts contain &mdash; and we&rsquo;d rather you start the conversation knowing the truth than spend money to learn what&rsquo;s wrong.
              </p>
              <p className="text-[#F7F6F2]/50 text-xs mt-5 italic max-w-2xl mx-auto">
                Resolution work, if you choose to engage us for it after the review, is a separate engagement priced fairly to the case. There is no obligation. Past results do not guarantee future outcomes.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ── SECTION B: VERIFY US ── */}
        <section className="py-16 bg-[#F7F6F2]" data-testid="verify-us-section">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.55 }} className="text-center mb-10">
              <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-semibold text-[#0B2240] mb-3">
                Verify us where you already trust the verdict.
              </h2>
              <p className="text-[#4C5A70] text-sm max-w-2xl mx-auto">
                Don&rsquo;t take our word for it. Our practice is publicly listed on the platforms below and registered with regulatory bodies that hold us accountable.
              </p>
            </motion.div>

            {/* Platform row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8" data-testid="verify-platforms">
              {[
                { label: "Find us on Google", sublabel: "Reviews &middot; Hours &middot; Maps", href: "#" },
                { label: "KGOB on LinkedIn", sublabel: "Company profile &middot; Team", href: "#" },
                { label: "KGOB on Facebook", sublabel: "Updates &middot; Community", href: "#" },
              ].map((p, i) => (
                <a
                  key={i}
                  href={p.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white border border-[#E1DFD7] rounded-md px-5 py-4 hover:border-[#D4AF37] hover:shadow-sm transition-all text-center"
                  data-testid={`verify-platform-${i}`}
                >
                  <div className="font-semibold text-[#0B2240] text-sm" dangerouslySetInnerHTML={{ __html: p.label }} />
                  <div className="text-[11px] text-[#4C5A70] mt-1" dangerouslySetInnerHTML={{ __html: p.sublabel }} />
                </a>
              ))}
            </div>

            {/* Verifiable credentials block */}
            <div className="bg-white border border-[#E1DFD7] rounded-lg p-6 sm:p-8" data-testid="verify-credentials">
              <div className="flex items-center gap-3 mb-5">
                <Award className="w-5 h-5 text-[#D4AF37]" />
                <span className="font-['Inter'] text-[11px] font-semibold tracking-[0.22em] uppercase text-[#D4AF37]">
                  Independently verifiable
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                  <div className="font-semibold text-[#0B2240] text-sm mb-1">NC State Board of CPA Examiners</div>
                  <p className="text-xs text-[#4C5A70] leading-relaxed">
                    KGOB CPAs &amp; Advisors PLLC is a licensed firm registered with the North Carolina State Board of CPA Examiners. Verify any NC CPA or firm at <code className="bg-[#F7F6F2] px-1 py-0.5 rounded text-[10px]">nccpaboard.gov</code>.
                  </p>
                </div>
                <div>
                  <div className="font-semibold text-[#0B2240] text-sm mb-1">AICPA Peer Review System</div>
                  <p className="text-xs text-[#4C5A70] leading-relaxed">
                    All licensed NC CPA firms providing assurance services are required to participate in the AICPA Peer Review program. Our peer review history is part of the public record.
                  </p>
                </div>
                <div>
                  <div className="font-semibold text-[#0B2240] text-sm mb-1">IRS PTIN-Registered Practitioners</div>
                  <p className="text-xs text-[#4C5A70] leading-relaxed">
                    Every tax preparer on our team holds an active IRS Preparer Tax Identification Number. PTINs are required for paid preparation and verifiable on the IRS PTIN registry.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── SECTION C: WHAT THE REPORT TYPICALLY UNCOVERS ── */}
        <section className="py-20 bg-white" data-testid="case-studies-section">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.55 }} className="text-center mb-12">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="h-px w-8 bg-[#D4AF37]" />
                <span className="font-['Inter'] text-[11px] font-semibold tracking-[0.22em] uppercase text-[#D4AF37]">
                  What the report uncovers
                </span>
                <span className="h-px w-8 bg-[#D4AF37]" />
              </div>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#0B2240] leading-tight">
                Three reviews. Three discoveries.
              </h2>
              <p className="text-[#4C5A70] mt-4 max-w-2xl mx-auto">
                What each review actually surfaced &mdash; and the moment the client realized they could finally see the full picture.
              </p>
            </motion.div>

            <div className="space-y-6" data-testid="case-studies-list">
              {[
                {
                  title: "The phantom assessment",
                  archetype: "Retired teacher &middot; Mooresville, NC",
                  story: "She thought her record was clean. Her free Account Review surfaced six IRS-filed substitute returns under §6020(b) for years she&rsquo;d never actually filed, inflating her assessed balance to roughly $42,000. After we filed the actual returns from her records, her genuine liability was just over $9,000. The Account Review made the difference visible.",
                  outcome: "The report identified the problem in 8 days. She chose to engage us for resolution; the IRS accepted an Offer in Compromise.",
                },
                {
                  title: "The trust-fund surprise",
                  archetype: "HVAC contractor &middot; Charlotte metro",
                  story: "He pulled an Account Review after a vague IRS notice arrived. The report uncovered approximately $187,000 of Trust Fund Recovery Penalties personally assessed against him under §6672, dating to three quarters across 2019&ndash;2021. He had no idea the personal liability existed.",
                  outcome: "With the picture in hand, he engaged us to negotiate. We secured an Installment Agreement structured to his actual cash flow within 11 months.",
                },
                {
                  title: "The unfiled-return cliff",
                  archetype: "Small construction firm &middot; Triad NC",
                  story: "The owner pulled a free review out of curiosity. The transcript showed roughly $310,000 in unpaid payroll tax balance plus active §6672 personal exposure on him. A CPA on our team flagged the case within 48 hours of the report being generated.",
                  outcome: "He engaged us to bring filings current and structure a resolution. The business stayed open, employees stayed on payroll, personal asset seizure was avoided.",
                },
              ].map((c, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.5, delay: 0.1 * i }}
                  data-testid={`case-study-${i}`}
                >
                  <Card className="p-6 sm:p-8 border border-[#E1DFD7] hover:border-[#D4AF37]/40 transition-colors">
                    <div className="flex items-start gap-4">
                      <Quote className="w-7 h-7 text-[#D4AF37] flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-['Inter'] text-[10px] tracking-[0.18em] uppercase text-[#D4AF37] font-semibold">Story {i + 1}</span>
                          <span className="text-[#4C5A70]/40">&middot;</span>
                          <span className="text-[11px] text-[#4C5A70]" dangerouslySetInnerHTML={{ __html: c.archetype }} />
                        </div>
                        <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">{c.title}</h3>
                        <p className="text-sm text-[#4C5A70] leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: c.story }} />
                        <p className="text-sm text-[#0B2240] italic font-medium leading-relaxed border-l-2 border-[#D4AF37] pl-4">
                          {c.outcome}
                        </p>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>

            <p className="text-xs text-[#4C5A70]/65 text-center mt-8 italic max-w-2xl mx-auto leading-relaxed" data-testid="case-studies-disclaimer">
              Composites drawn from real client engagements. Identifying details (name, location, industry, amounts) have been changed or generalized to protect client confidentiality. Past results do not guarantee future outcomes. The IRS evaluates every Offer in Compromise, Installment Agreement, and penalty abatement request on its specific facts.
            </p>
          </div>
        </section>

        {/* ── INLINE CTA #1 — after case studies (empathy peak) ── */}
        <InlineCTA
          source="irs-review-after-cases"
          eyebrow="Your review starts here"
          title="See what your transcript actually says."
          helper="Free &middot; prepared by a licensed CPA &middot; no obligation"
        />

        {/* ── PROBLEM FRAMING ── */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#0B2240] mb-6 text-center">
                You can't fix what you can't see.
              </h2>
              <div className="max-w-3xl mx-auto space-y-4 text-[#4C5A70] leading-relaxed">
                <p>
                  The IRS keeps its own version of your tax history — and it doesn't always match what you think you filed.
                  Balances you didn't know about. Penalties compounding quietly. Returns the IRS says they never received.
                  Payments they didn't apply. Until you see exactly what the IRS sees, you're operating blind.
                </p>
                <p>
                  An IRS Account Review pulls data directly from the IRS — the same records they use internally — and
                  translates them into a clear, readable report prepared by a licensed CPA. Not a credit report.
                  The IRS's own record of your tax account, decoded so you can actually understand it.
                </p>
                <p className="font-medium text-[#0B2240]">
                  And once your CPA has the full picture, they'll tell you exactly what needs to happen next —
                  and handle the resolution for you.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── WHEN YOU NEED THIS ── */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#0B2240] mb-4">
                The Right Time to Get Your Account Reviewed
              </h2>
              <p className="text-[#4C5A70]">If any of these apply to you — you need clarity before you act.</p>
            </motion.div>

            <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { icon: Mail, title: "Received an IRS Notice", desc: "Don't respond blind. Know exactly what the IRS is claiming before you write a single word." },
                { icon: DollarSign, title: "Know You Owe Back Taxes", desc: "You know there's a balance — but do you know the exact amount, penalty breakdown, and all eligible relief options?" },
                { icon: FileText, title: "Haven't Filed in Years", desc: "The IRS may have already filed substitute returns on your behalf — often with no deductions and inflated balances." },
                { icon: Home, title: "Buying, Selling, or Refinancing", desc: "Federal tax liens attach to property and can collapse real estate transactions. Catch them before your closing date." },
                { icon: Briefcase, title: "Business Sale or Acquisition", desc: "IRS liability transfers in asset sales can surprise buyers and sellers alike. Confirm IRS standing before the deal closes." },
                { icon: CheckCircle, title: "Routine Compliance Check", desc: "Even if you believe everything is in order — a CPA-reviewed account confirmation removes all doubt." }
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <motion.div key={i} variants={fadeUp}>
                    <Card className="p-5 border border-[#E1DFD7] h-full hover:border-[#D4AF37]/30 transition-colors">
                      <Icon className="w-5 h-5 text-[#D4AF37] mb-3" />
                      <h3 className="font-semibold text-[#0B2240] mb-1.5">{item.title}</h3>
                      <p className="text-sm text-[#4C5A70]">{item.desc}</p>
                    </Card>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>

        {/* ── WHAT YOUR REVIEW REVEALS ── */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#0B2240] mb-4">
                What Your Account Review Reveals
              </h2>
              <p className="text-[#4C5A70] max-w-2xl mx-auto">
                Your CPA pulls your official IRS records and translates them into a report that covers:
              </p>
            </motion.div>

            <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { title: "Balances Owed", desc: "What the IRS says you owe — and for which tax years" },
                { title: "Filing Status", desc: "Whether all your returns have been received and processed" },
                { title: "Penalties & Interest", desc: "Any penalties, interest, or adjustments applied to your account" },
                { title: "Payment History", desc: "All payments and credits the IRS has on file" },
                { title: "Collection Status", desc: "Active liens, levies, installment agreements, or garnishments" },
                { title: "Discrepancies", desc: "Differences between what you filed and what the IRS recorded" }
              ].map((item, i) => (
                <motion.div key={i} variants={fadeUp}>
                  <Card className="p-5 border border-[#E1DFD7] h-full hover:border-[#D4AF37]/30 transition-colors">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mb-3" />
                    <h3 className="font-semibold text-[#0B2240] mb-1.5">{item.title}</h3>
                    <p className="text-sm text-[#4C5A70]">{item.desc}</p>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ── WHO NEEDS THIS ── */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#0B2240] mb-4">
                For Every Client Type
              </h2>
              <p className="text-[#4C5A70]">The IRS keeps separate records for individuals, businesses, and trusts & estates. Our CPAs know the difference.</p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: Users, title: "Individuals",
                  items: [
                    "Received an IRS notice or letter",
                    "Owe back taxes from prior years",
                    "Haven't filed in multiple years",
                    "Buying or selling a home (lien check)",
                    "Want confirmation your account is clean"
                  ]
                },
                {
                  icon: Building, title: "Business Owners",
                  items: [
                    "Payroll / 941 tax balance issues",
                    "Business income tax liabilities",
                    "SBA loan IRS transcript requirements",
                    "Trust Fund Recovery Penalty (TFRP) exposure",
                    "Pre-sale due diligence on IRS standing"
                  ]
                },
                {
                  icon: Landmark, title: "Trusts & Estates",
                  items: [
                    "Estate administration & executor duties",
                    "Unfiled Form 1041 trust returns",
                    "Estate tax compliance & IRS liens",
                    "Beneficiary IRS clearance before distribution",
                    "Prior trustee liability review"
                  ]
                }
              ].map((group, gi) => {
                const Icon = group.icon;
                return (
                  <motion.div key={gi} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: gi * 0.1 }}>
                    <Card className="p-6 border border-[#E1DFD7] h-full">
                      <div className="w-10 h-10 rounded-lg bg-[#0B2240] flex items-center justify-center mb-4">
                        <Icon className="h-5 w-5 text-[#D4AF37]" />
                      </div>
                      <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#0B2240] mb-4">{group.title}</h3>
                      <ul className="space-y-2.5">
                        {(group.items || []).map((item, i) => (
                          <li key={i} className="flex gap-2.5">
                            <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-[#4C5A70]">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ── */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#0B2240]">
                Request. Review. Get Help.
              </h2>
            </motion.div>

            <div className="space-y-5">
              {[
                { step: '1', title: 'Request Your Free Review', desc: 'Submit your request online and share your basic details. That\'s all we need to pull your complete IRS file — the exact records the IRS has on you right now. No cost, no obligation.' },
                { step: '2', title: 'A Real CPA Reads Every Line', desc: 'A licensed CPA at KGOB goes through your entire IRS record — every balance, every penalty, every year, every flag. Then they write it up in plain English so you actually understand it.' },
                { step: '3', title: 'Authorize IRS Access', desc: 'Sign IRS Form 2848 electronically to authorize our CPAs to access your records on your behalf. Standard IRS-approved process.' },
                { step: '4', title: 'Receive Your Review & Next Steps', desc: 'Once the IRS releases your records, we analyze everything and deliver your report through your secure portal. Your CPA walks you through exactly what it means and what to do next.' }
              ].map((item, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                  <Card className="p-6 border border-[#E1DFD7]">
                    <div className="flex gap-4 items-start">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                        <span className="text-[#0B2240] font-bold text-lg">{item.step}</span>
                      </div>
                      <div>
                        <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-1.5">{item.title}</h3>
                        <p className="text-[#4C5A70] text-sm leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── INLINE CTA #2 — after Process section (highest intent peak: friction is at minimum) ── */}
        <InlineCTA
          source="irs-review-after-process"
          variant="dark"
          eyebrow="You know what happens next"
          title="Now take the first step."
          helper="Most reports delivered within days &middot; secure portal &middot; no obligation"
        />

        {/* ── WE REVIEW. WE EXPLAIN. WE RESOLVE. ── */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#0B2240] mb-6 text-center">
                We Review. We Explain. We Resolve.
              </h2>

              <Card className="p-8 border-2 border-[#D4AF37]/20 bg-[#D4AF37]/[0.03]">
                <div className="space-y-4 text-[#4C5A70] leading-relaxed">
                  <p>
                    Your account review doesn't just sit in a folder. Your CPA reviews it and walks you through exactly what it means —
                    what the IRS has on file, where the problems are, and what your options look like.
                  </p>
                  <p>
                    If something needs fixing — reducing penalties, setting up a payment plan, filing missing returns, negotiating a settlement, or removing a lien —
                    <span className="text-[#0B2240] font-medium"> we handle that.</span> KGOB is a full-service CPA firm with
                    an in-house IRS representation team. We don't hand you a report and wish you luck. We're here for everything
                    that comes after.
                  </p>
                  <p>
                    Your account review is completely free — no cost, no obligation. If issues are found and you'd like help resolving them,
                    that's a separate conversation. What resolution costs depends entirely on what's found and what needs to be done.
                    Your CPA will explain everything clearly before any work begins. No pressure. No surprises.
                  </p>
                </div>

                <div className="mt-6 pt-6 border-t border-[#D4AF37]/15">
                  <div className="grid sm:grid-cols-3 gap-4">
                    {[
                      { label: "Penalty Reduction", desc: "First-time abatement and reasonable cause relief" },
                      { label: "Payment Plans", desc: "Installment agreements with the IRS" },
                      { label: "Settlement Options", desc: "Offers in Compromise and Currently Not Collectible status" }
                    ].map((item, i) => (
                      <div key={i} className="flex gap-2.5">
                        <ShieldCheck className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-[#0B2240]">{item.label}</p>
                          <p className="text-xs text-[#4C5A70]">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* ── WHAT WE UNCOVER ── */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#0B2240] mb-4">
                Issues Our CPAs Regularly Identify
              </h2>
              <p className="text-[#4C5A70] max-w-2xl mx-auto">
                These are the problems hiding in IRS records — missed by most people until a licensed professional looks.
              </p>
            </motion.div>

            <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: DollarSign, title: "Unpaid Balances Accruing Interest", desc: "IRS interest compounds daily. Many clients discover balances far larger than expected." },
                { icon: AlertTriangle, title: "Failure-to-File & Pay Penalties", desc: "Among the most common — and most removable — IRS penalties." },
                { icon: Scale, title: "Federal Tax Liens", desc: "Filed when balances go unpaid, liens attach to property and appear on credit records." },
                { icon: FileText, title: "IRS-Prepared Substitute Returns", desc: "When returns aren't filed, the IRS prepares them — typically with no deductions." },
                { icon: Shield, title: "Trust Fund Recovery Penalties", desc: "Payroll tax failures can result in personal liability for business owners." },
                { icon: Clock, title: "Collection Statute Expiration", desc: "The IRS has 10 years to collect. Sometimes debts are closer to expiration than you think." },
                { icon: ClipboardCheck, title: "Unfiled Estate or Trust Returns", desc: "Executors and trustees often inherit unfiled Form 1041 obligations." },
                { icon: Ban, title: "Wage Garnishments & Bank Levies", desc: "Imminent IRS enforcement that can freeze accounts or garnish wages." }
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <motion.div key={i} variants={fadeUp}>
                    <Card className="p-5 border border-[#E1DFD7] h-full hover:border-[#D4AF37]/30 transition-colors">
                      <Icon className="w-5 h-5 text-[#D4AF37] mb-3" />
                      <h3 className="font-semibold text-[#0B2240] text-sm mb-1.5">{item.title}</h3>
                      <p className="text-xs text-[#4C5A70]">{item.desc}</p>
                    </Card>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>

        {/* ── WHY KGOB ── */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#0B2240]">
                Why KGOB?
              </h2>
              <p className="mt-3 text-[#4C5A70]">This isn't a faceless online service. It's your CPA firm.</p>
            </motion.div>

            <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                "Licensed CPAs with IRS representation experience",
                "Charlotte, NC-based firm serving clients nationwide",
                "Secure, encrypted portal for all communications",
                "Direct access to IRS CAF system for record retrieval",
                "Same team that handles notice response and audit protection",
                "Full-service firm — resolution help available after your review"
              ].map((item, i) => (
                <motion.div key={i} variants={fadeUp} className="flex gap-3 items-start">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-[#4C5A70]">{item}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ── INLINE CTA #3 — above FAQs (qualified reader looking for objections) ── */}
        <InlineCTA
          source="irs-review-pre-faq"
          eyebrow="Still have questions?"
          title="We address your specific situation in the report's cover letter."
          helper="The fastest way to get a question answered about your account is to see your account."
        />

        {/* ── FAQ ── */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#0B2240]">
                Your Questions, Answered
              </h2>
            </motion.div>
            <div className="space-y-3">
              {faqs.map((faq, i) => <FAQItem key={i} faq={faq} index={i} />)}
            </div>
          </div>
        </section>

        {/* ── FOOTER CTA ── */}
        <section className="py-20 bg-gradient-to-b from-[#0B2240] to-[#0f2d4f]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F7F6F2] mb-4">
                Stop Guessing. Get the Full Picture — Free.
              </h2>
              <p className="text-lg text-[#F7F6F2]/70 mb-8 max-w-2xl mx-auto">
                A licensed CPA pulls your IRS file and tells you exactly what's on it — in plain English, at no cost to you. If something needs fixing, our CPA team handles that too. No cost. No obligation. First things first: get the picture.
              </p>
              <Button asChild className="h-14 px-10 rounded-xl bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] font-semibold text-base shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] hover:brightness-110" data-testid="footer-cta">
                <a data-testid="i-r-s-transcript-report-a-1" href={ctaUrl('irs-review-final')} target="_blank" rel="noopener noreferrer">
                  Get Your Free IRS Account Review
                  <ArrowRight className="h-5 w-5 ml-2" />
                </a>
              </Button>
            </motion.div>
          </div>
        </section>
      </main>

      <StickyCTA />

      <Footer />
    </div>
  );
};

export default IRSTranscriptReport;
